import type {
  DocumentResponse,
  DocumentsListing,
  DocumentsResponse,
} from '@contracts/document/DocumentResponse';
import { AuthenticatedClient } from '@src/common/services/AuthenticatedClient';
import { AxiosResponseConverter } from '@src/documents/AxiosResponseConverter';
import type { Document } from '@src/orders/domain/PermitApplication';

export enum DocumentParentType {
  ORDER = 'ORDER',
  PERMIT_APPLICATION = 'PERMIT_APPLICATION',
}
type CustomDocumentInformation = {
  permitType: string;
  permitName: string;
  permitJurisdiction: string;
};

export const DocumentsClient = {
  findAllForOrder: async (orderId: string): Promise<DocumentsListing[]> => {
    const { data: response } = await AuthenticatedClient.get<DocumentsResponse>(
      `/documents?orderId=${orderId}`,
    );

    return parseDocumentsResponse(response);
  },

  downloadWithId: async (
    documentId: string,
  ): Promise<{ blob: Blob; filename: string }> => {
    const response = await AuthenticatedClient.get(`/documents/${documentId}`, {
      responseType: 'blob',
    });

    const filename =
      response.headers['content-disposition']
        ?.split('filename=')[1]
        ?.replace(/['"]/g, '') || 'downloaded-file';
    const blob = AxiosResponseConverter.toBlob(response);

    return { blob, filename };
  },

  downloadWithKey: async (
    documentKey: string,
  ): Promise<{ blob: Blob; filename: string }> => {
    const response = await AuthenticatedClient.get(`/document`, {
      params: { key: documentKey },
      responseType: 'blob',
    });

    const filename =
      response.headers['content-disposition']
        ?.split('filename=')[1]
        ?.replace(/['"]/g, '') || 'downloaded-file';
    const blob = AxiosResponseConverter.toBlob(response);

    return { blob, filename };
  },

  upload: async (data: {
    file: File;
    permitApplicationId?: string;
    orderId?: string;
    customDocumentInformation?: CustomDocumentInformation;
    sendToClient?: boolean;
  }): Promise<Document> => {
    const formData = new FormData();
    formData.append('file', data.file);
    if (data.orderId) {
      formData.append('orderId', data.orderId);
    }
    if (data.permitApplicationId) {
      formData.append('permitApplicationId', data.permitApplicationId);
    }
    if (data.customDocumentInformation) {
      formData.append('permitType', data.customDocumentInformation.permitType);
      formData.append(
        'permitJurisdiction',
        data.customDocumentInformation.permitJurisdiction,
      );
      formData.append('permitName', data.customDocumentInformation.permitName);
    }
    if (data.sendToClient !== undefined) {
      formData.append('sendToClient', String(data.sendToClient));
    }

    const { data: response } = await AuthenticatedClient.post<DocumentResponse>(
      'documents:upload',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    );

    return {
      documentId: response.id,
      key: response.key,
      displayName: response.displayName,
      sendToClient: response.sendToClient,
      showToClient: response.showToClient,
    };
  },

  uploadStatePermit: async (data: {
    file: File;
    permitApplicationId: string;
    orderId: string;
  }): Promise<Document> => {
    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('orderId', data.orderId);
    formData.append('permitApplicationId', data.permitApplicationId);

    const { data: response } = await AuthenticatedClient.post<DocumentResponse>(
      'documents:uploadStatePermit',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    );

    return {
      documentId: response.id,
      key: response.key,
      sendToClient: response.sendToClient,
      showToClient: response.showToClient,
    };
  },

  delete: async (id: string): Promise<void> => {
    await AuthenticatedClient.delete(`/documents/${id}`);
  },

  rename: async (id: string, name?: string): Promise<void> => {
    await AuthenticatedClient.put(`/documents/${id}/rename`, { name });
  },

  markToSend: async (id: string, sendToClient: boolean): Promise<void> => {
    await AuthenticatedClient.post(`/documents/${id}:markToSend`, {
      sendToClient,
    });
  },

  markToShowToClient: async (
    id: string,
    showToClient: boolean,
  ): Promise<void> => {
    await AuthenticatedClient.post(`/documents/${id}:markToShowToClient`, {
      showToClient,
    });
  },
};

const parseDocumentsResponse = (
  response: DocumentsResponse,
): DocumentsListing[] => {
  return response.documents.map((document) => ({
    ...document,
    parent: {
      id: document.parent.id,
      name: document.parent.name,
      type: DocumentParentType[document.parent.type],
    },
  }));
};
